export default function () {
    const schemaKeywords = {};

    // Utility functions for keyword generation
    const keywordUtils = {
        INSTRUMENTS: {
            'electric': 'electric guitar',
            'acoustic': 'acoustic guitar',
            'ukulele': 'ukulele'
        },

        getInstrumentLabel(instrument) {
            return this.INSTRUMENTS[instrument?.toLowerCase()] || 'guitar';
        },

        getDifficultyPlural (difficulty) {
            switch (difficulty) {
                case 'beginner':
                    return 'beginners';
                case 'improver':
                    return 'improvers';
                case 'intermediate':
                    return 'intermediates';
                case 'advanced':
                    return 'advanced players';
                default:
                    return 'all levels';
            }
        }
    };

    schemaKeywords.generateKeywords = {
        course(course) {
            if (!course) return '';

            const firstLesson = course.course_lessons?.[0];
            const instrumentLabel = keywordUtils.getInstrumentLabel(firstLesson?.lesson_instrument);
            const difficulty = firstLesson?.lesson_difficulty?.toLowerCase() || 'all levels';
            const difficultyPlural = keywordUtils.getDifficultyPlural(difficulty);

            const baseKeywords = new Set([
                `${difficulty} ${instrumentLabel} lessons`,
                `learn ${instrumentLabel} online`,
                `${instrumentLabel} course online`,
                `${difficulty} ${instrumentLabel} course`,
                `best ${instrumentLabel} lessons`,
                `${instrumentLabel} lessons for ${difficultyPlural}`,
                `learn to play ${instrumentLabel}`,
                `online ${instrumentLabel} lessons`,
                `structured ${instrumentLabel} lessons`,
                `step by step ${instrumentLabel} lessons`,
                `comprehensive ${instrumentLabel} tutorial`,
                `${difficulty} ${instrumentLabel} curriculum`,
                'online music lessons',
                `professional ${instrumentLabel} instruction`,
                'andy guitar tutorials',
                `andy guitar ${instrumentLabel} course`
            ]);

            // Remove duplicates while maintaining order
            return [...baseKeywords]
                .join(', ');
        },

        lesson(lesson) {
            if (!lesson) return '';

            // Helper function for lesson category specific keywords
            function getLessonCategoryKeywords(lesson, instrumentLabel) {
                const category = lesson.lesson_category?.toLowerCase();

                switch(category) {
                    case 'song':
                        return [
                            `${lesson.lesson_title} ${instrumentLabel}`,
                            `${lesson.lesson_title} tutorial`,
                            `${instrumentLabel} song tutorial`,
                            `learn ${instrumentLabel} songs online`,
                            `${instrumentLabel} cover`,
                            `learn ${instrumentLabel} songs`,
                        ];

                    case 'lesson':
                        return [
                            `${lesson.lesson_title} ${instrumentLabel}`,
                            `${instrumentLabel} lesson`,
                            `${instrumentLabel} technique`,
                            `${instrumentLabel} tutorial`,
                            `improve ${instrumentLabel} playing`,
                            `${instrumentLabel} learning`,
                            `${instrumentLabel} instruction`,
                            `${instrumentLabel} tips`
                        ];

                    case 'live replay':
                        return [
                            `live ${instrumentLabel} lesson`,
                            `${instrumentLabel} masterclass`,
                            `online ${instrumentLabel} workshop`,
                            `interactive ${instrumentLabel} lesson`,
                            `${instrumentLabel} Q&A`,
                            'live music lesson',
                            `${instrumentLabel} live stream`,
                            `andy guitar live ${instrumentLabel}`
                        ];

                    default:
                        return [
                            `${lesson.lesson_title} ${instrumentLabel}`,
                            `${instrumentLabel} tutorial`,
                            'online music lesson'
                        ];
                }
            }

            // Helper function for special lesson types (chord songs & top lists)
            function getLessonTypeKeywords(lesson, instrumentLabel) {
                if (!lesson.lesson_type) return [];

                const type = lesson.lesson_type.toLowerCase();

                if (type.includes('chord song')) {
                    const chordCount = type.split(' ')[0];
                    return [
                        `${chordCount} chord songs`,
                        `easy ${chordCount} chord songs`,
                        `${chordCount} chord ${instrumentLabel} songs`,
                        'simple guitar songs',
                        'easy songs to learn',
                        `easy songs for ${instrumentLabel}`
                    ];
                }

                if (type === 'top list') {
                    return [
                        'best songs to learn',
                        `best ${instrumentLabel} songs`,
                        'popular songs to learn',
                        `popular ${instrumentLabel} songs`,
                        'top songs for beginners',
                        `greatest ${instrumentLabel} songs`,
                        'must learn songs',
                        `essential ${instrumentLabel} songs`
                    ];
                }

                return [];
            }

            const instrumentLabel = keywordUtils.getInstrumentLabel(lesson.lesson_instrument);
            const difficulty = lesson.lesson_difficulty?.toLowerCase() || 'all levels';
            const difficultyPlural = keywordUtils.getDifficultyPlural(difficulty);
            const skill = lesson.lesson_skill?.toLowerCase();
            const genre = lesson.lesson_genre?.toLowerCase();
            const decade = lesson.lesson_decade?.toLowerCase();
            const artist = lesson.lesson_artist;

            const keywords = new Set([
                // Core keywords based on lesson category
                ...getLessonCategoryKeywords(lesson, instrumentLabel),
                // Special lesson types (chord songs & top lists)
                ...getLessonTypeKeywords(lesson, instrumentLabel),

                // Artist specific
                artist ? `${artist} ${instrumentLabel} lesson` : null,
                artist ? `how to play ${artist} songs` : null,
                artist ? `${artist} song tutorial` : null,

                // Decade specific
                decade ? `${decade} ${instrumentLabel} songs` : null,
                decade ? `${decade} music` : null,

                // Genre specific
                genre ? (genre.toLowerCase() === 'acoustic' ? 'acoustic guitar' : `${genre} ${instrumentLabel}`) : null,
                genre ? `learn ${genre}` : null,
                genre ? `${genre} tutorial` : null,

                // Skill specific
                skill ? `${skill} tutorial` : null,
                skill ? `learn ${skill}` : null,
                skill ? `${instrumentLabel} ${skill}` : null,

                // Instrument specific
                instrumentLabel,
                `learn ${instrumentLabel}`,

                // Difficulty specific
                `${difficulty} ${instrumentLabel}`,
                `${instrumentLabel} for ${difficultyPlural}`
            ]);

            // Remove null values and join to comma-separated string
            return [...keywords].filter(Boolean).join(', ');
        }
    };

    return schemaKeywords;
};
